// 儀表板 — HeroUI 官方組件版 (Card / Table / Button / Chip)
// 圖表: HeroUI v3 尚未提供 Charts 組件 (@heroui/chart 未發佈) — 按規則 5 用 minimal recharts
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Chip, Table, TableContent, TableHeader, TableColumn, TableBody, TableRow, TableCell } from '@heroui/react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { api, money, fmtDate, ORDER_STATUS, QT_STATUS } from '../api.js';
import { ChartTip } from '../charts.jsx';

const DONUT_HEX = ['#ededed', '#a8a8a8', '#747474', '#d0d0d0', '#909090'];

function Trend({ v }) {
  if (v === null) return <p className="text-xs text-gray-400">— 對比上月</p>;
  return v >= 0
    ? <p className="text-xs text-success-600 font-semibold">↑ {v}% vs 上月</p>
    : <p className="text-xs text-gray-500 font-semibold">↓ {Math.abs(v)}% vs 上月</p>;
}

class EB extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() { return this.state.err ? <pre id="errbd" style={{ whiteSpace: 'pre-wrap', padding: 24, color: 'var(--ink)' }}>{String(this.state.err && (this.state.err.stack || this.state.err.message) || this.state.err)}</pre> : this.props.children; }
}

function DashboardInner() {
  const [d, setD] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [rng, setRng] = useState(12);
  const [supRows, setSupRows] = useState([]);
  const nav = useNavigate();
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get('/dashboard').then(setD).catch(() => setD(false));
    api.get(`/reports/monthly?year=${year}`).then(r => setMonthly(r.rows || [])).catch(() => setMonthly([]));
    api.get(`/reports/suppliers?year=${year}`).then(r => setSupRows((r.rows || []).filter(x => (x.po_total || 0) > 0))).catch(() => setSupRows([]));
  }, []);

  if (d === null) return <div className="p-10 text-gray-500 font-semibold">載入中…</div>;
  if (d === false) return <div className="p-10 text-gray-500 font-semibold">無法連接後端，請確認伺服器已啟動</div>;

  const cm = new Date().getMonth();
  const mRow = monthly[cm] || null;
  const pRow = cm > 0 ? monthly[cm - 1] : null;
  const trendOf = (cur, prev) => (prev > 0 ? Math.round((cur - prev) / prev * 100) : null);
  const recvTrend = trendOf(mRow?.received || 0, pRow?.received || 0);
  const chartRows = monthly.slice(-rng);

  const byCustomer = Object.values(
    (d.open_invoices || []).reduce((acc, inv) => {
      const k = inv.customer_name;
      acc[k] = acc[k] || { name: k, outstanding: 0 };
      acc[k].outstanding += inv.adjusted_amount - inv.paid_amount;
      return acc;
    }, {})
  ).sort((a, b) => b.outstanding - a.outstanding).slice(0, 6);

  const donut = supRows.slice(0, 5);
  const donutTotal = donut.reduce((s, x) => s + x.po_total, 0);

  const stats = [
    { icon: '💰', label: '應收未收帳款', value: money(d.outstanding), sub: d.overdue > 0 ? `逾期 HK$ ${money(d.overdue)}` : '暫無逾期', trend: null, to: '/ar' },
    { icon: '✅', label: '本月已收款', value: money(d.received_month), sub: null, trend: recvTrend, to: '/invoices' },
    { icon: '🏗️', label: '進行中工程', value: String(d.open_orders), sub: `${d.draft_quotations} 張報價待跟進`, trend: null, to: '/orders' },
    { icon: '📈', label: '本年毛利 (開票 − 採購)', value: money(d.profit_ytd), sub: `應付未付 $${money(d.ap_outstanding)}`, trend: null, to: '/reports' },
  ];

  return (
    <div className="mono-dashboard space-y-6">
      {/* Hero */}
      <div className="rounded-2xl bg-gray-900 text-white p-7 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">今日業務總覽 👋</h1>
          <p className="text-sm text-gray-300 mt-1 max-w-xl">報價 → 訂單 → 發票 → 收款 → 採購全流程自動化。所有未找數嘅發票已自動結算入客戶對帳單。</p>
        </div>
        <div className="flex gap-3">
          <Button className="mono-primary" color="primary" onPress={() => nav('/quotations/new')}>📄 新報價單</Button>
          <Button variant="bordered" className="border-gray-500 text-white" onPress={() => nav('/ar')}>💰 追數中心</Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <Card key={i} isPressable onPress={() => nav(s.to)} className={`border border-gray-200 shadow-sm ${['kpi-receivables', 'kpi-received', 'kpi-projects', 'kpi-profit'][i]}`}>
            <CardContent className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-lg flex-shrink-0">{s.icon}</div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{s.label}</p>
                <p className={`text-2xl font-bold text-gray-900 leading-tight ${i === 3 && d.profit_ytd < 0 ? 'attention-note' : ''}`}>{s.label.includes('工程') ? s.value : <>HK$ {s.value}</>}</p>
                {s.trend !== null ? <Trend v={s.trend} /> : <p className={`text-xs text-gray-400 ${i === 0 && d.overdue > 0 ? 'attention-note' : ''}`}>{s.sub}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* 負毛利工程警示 */}
      {(d.projects_at_risk || []).length > 0 ? (
        <Card className="risk-notice border border-gray-200 shadow-sm">
          <CardContent>
            <p className="text-sm font-bold text-gray-900 mb-2">🚨 工程警示 — 開票低於分判成本 ({d.projects_at_risk.length})</p>
            {d.projects_at_risk.map(o => (
              <div key={o.id} className="flex justify-between items-center text-[13px] py-2 border-b border-dashed border-gray-100 cursor-pointer" onClick={() => nav('/orders/' + o.id)}>
                <span><span className="font-bold text-gray-900">{o.order_no}</span> {o.title} — {o.customer_name}</span>
                <span className="text-gray-500">已開票 <b className="text-gray-900">HK$ {money(o.invoiced)}</b> ＜ 採購 <b>HK$ {money(o.po_cost)}</b></span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* 進行中工程 — HeroUI Table */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="flex justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">🏗️</div>
            <div>
              <CardTitle>進行中工程 — 而家做緊嘅嘢</CardTitle>
              <CardDescription>{d.active_orders.length} 項工程進行中</CardDescription>
            </div>
          </div>
          <Button size="sm" variant="flat" onPress={() => nav('/orders')}>全部工程 →</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table aria-label="進行中工程" removeWrapper>
            <TableContent>
            <TableHeader>
              <TableColumn isRowHeader>訂單</TableColumn>
              <TableColumn>工程</TableColumn>
              <TableColumn>客戶</TableColumn>
              <TableColumn className="num">合約額</TableColumn>
              <TableColumn className="num">已開票</TableColumn>
              <TableColumn className="num">分判成本</TableColumn>
              <TableColumn>狀態</TableColumn>
            </TableHeader>
            <TableBody emptyContent="沒有進行中工程 — 由報價單轉訂單開始">
              {d.active_orders.map(o => (
                <TableRow key={o.id} className="cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>
                  <TableCell><span className="font-bold text-gray-900">{o.order_no}</span></TableCell>
                  <TableCell>{o.title || '—'}</TableCell>
                  <TableCell>{o.customer_name}</TableCell>
                  <TableCell className="num">{money(o.total)}</TableCell>
                  <TableCell className="num">{money(o.invoiced)}</TableCell>
                  <TableCell className="num">{money(o.po_total)}</TableCell>
                  <TableCell><Chip className={`st ${ORDER_STATUS[o.status]?.cls || 'st-gray'}`} size="sm" variant="flat" color="default">{ORDER_STATUS[o.status]?.label || o.status}</Chip></TableCell>
                </TableRow>
              ))}
            </TableBody>
            </TableContent>
          </Table>
        </CardContent>
      </Card>

      {/* 開票 vs 收款 + KPI chips */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="flex justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">📈</div>
            <div>
              <CardTitle>開票 vs 收款表現 ({year})</CardTitle>
              <CardDescription>漸層面積圖 — 右上切換範圍</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-gray-700">● 開票 Invoiced</span>
            <span className="text-xs font-semibold text-gray-400">● 收款 Received</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end mb-2">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {[3, 6, 12].map(v => (
                <button key={v} onClick={() => setRng(v)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${rng === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
                  最近 {v} 個月
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="hIssued" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ededed" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#ededed" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="hRecv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a8a8a8" stopOpacity={0.20} />
                  <stop offset="100%" stopColor="#a8a8a8" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#414141" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10.5, fill: '#a8a8a8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10.5, fill: '#a8a8a8' }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? Math.round(v / 1000) + 'K' : v} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="issued" name="開票" stroke="#ededed" strokeWidth={2} fill="url(#hIssued)" />
              <Area type="monotone" dataKey="received" name="收款" strokeDasharray="5 4" stroke="#a8a8a8" strokeWidth={2} fill="url(#hRecv)" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase">本月開票 Invoiced</p>
              <p className="text-base font-bold text-gray-900">HK$ {money(mRow?.issued || 0)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase">本月收款 Received</p>
              <p className="text-base font-bold text-gray-900">HK$ {money(mRow?.received || 0)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase">年內總開票 Total</p>
              <p className="text-base font-bold text-gray-900">HK$ {money(monthly.reduce((s, x) => s + x.issued, 0))}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Donut + Receivables bar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">🥧</div>
              <div>
                <CardTitle>分判成本分佈 (Top 供應商)</CardTitle>
                <CardDescription>{year} 年</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {donut.length === 0 ? <p className="text-gray-400 text-center py-8">暫無採購數據</p> : (
              <>
                <ResponsiveContainer width="100%" height={185}>
                  <PieChart>
                    <Pie data={donut.map(x => ({ name: x.name, value: x.po_total }))} dataKey="value" nameKey="name"
                      innerRadius={52} outerRadius={80} paddingAngle={3} cornerRadius={6} stroke="none">
                      {donut.map((_, i) => <Cell key={i} fill={DONUT_HEX[i % DONUT_HEX.length]} />)}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div>
                  {donut.map((x, i) => (
                    <div key={i} className="flex justify-between items-center text-[13px] py-1.5 border-b border-dashed border-gray-100">
                      <span className="flex items-center gap-2 font-semibold text-gray-700">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DONUT_HEX[i % DONUT_HEX.length] }} />{x.name}
                      </span>
                      <span className="font-bold text-gray-900">HK$ {money(x.po_total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 mt-1 border-t-2 border-gray-100">
                    <span className="font-bold text-gray-900">總分判額</span>
                    <span className="font-bold text-gray-900 text-[15px]">HK$ {money(donutTotal)}</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">💰</div>
              <div>
                <CardTitle>應收未收分佈 (按客戶)</CardTitle>
                <CardDescription>合計 HK$ {money(d.outstanding)}</CardDescription>
              </div>
            </div>
            <Button size="sm" variant="flat" onPress={() => nav('/ar')}>追數中心 →</Button>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byCustomer} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: '#b0b0b0' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="outstanding" name="未收" radius={[4, 8, 8, 4]} barSize={20}>
                  {byCustomer.map((_, i) => <Cell key={i} fill={['#ededed', '#b0b0b0', '#a8a8a8', '#d0d0d0', '#858585', '#e0e0e0'][i % 6]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* 未收數發票 + 最近報價單 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">⚠️</div>
              <div>
                <CardTitle>未收數發票 (最先到期)</CardTitle>
                <CardDescription>{d.open_invoices.length} 張未清</CardDescription>
              </div>
            </div>
            <Button size="sm" variant="flat" onPress={() => nav('/ar')}>全部 →</Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table aria-label="未收數發票" removeWrapper>
            <TableContent>
              <TableHeader>
                <TableColumn isRowHeader>發票</TableColumn>
                <TableColumn>客戶</TableColumn>
                <TableColumn>到期</TableColumn>
                <TableColumn className="num">餘數</TableColumn>
              </TableHeader>
              <TableBody emptyContent="🎉 全部已收!">
                {d.open_invoices.map(i => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => nav('/invoices')}>
                    <TableCell><span className="font-bold text-gray-900">{i.invoice_no}</span></TableCell>
                    <TableCell>{i.customer_name}</TableCell>
                    <TableCell>{fmtDate(i.due_date)}</TableCell>
                    <TableCell className="num">{money(i.adjusted_amount - i.paid_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableContent>
            </Table>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">📄</div>
              <div>
                <CardTitle>最近報價單</CardTitle>
                <CardDescription>{d.recent_quotations.length} 張</CardDescription>
              </div>
            </div>
            <Button size="sm" variant="flat" onPress={() => nav('/quotations')}>全部 →</Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table aria-label="最近報價單" removeWrapper>
            <TableContent>
              <TableHeader>
                <TableColumn isRowHeader>編號</TableColumn>
                <TableColumn>客戶</TableColumn>
                <TableColumn className="num">金額</TableColumn>
                <TableColumn>狀態</TableColumn>
              </TableHeader>
              <TableBody emptyContent="沒有報價單">
                {d.recent_quotations.map(q => (
                  <TableRow key={q.id} className="cursor-pointer" onClick={() => nav(`/quotations/${q.id}`)}>
                    <TableCell><span className="font-bold text-gray-900">{q.quote_no}</span></TableCell>
                    <TableCell>{q.customer_name}</TableCell>
                    <TableCell className="num">{money(q.total)}</TableCell>
                    <TableCell><Chip className={`st ${QT_STATUS[q.status]?.cls || 'st-gray'}`} size="sm" variant="flat" color="default">{QT_STATUS[q.status]?.label || q.status}</Chip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableContent>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard() { return <EB><DashboardInner /></EB>; }
